import React, { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Failed to send reset email');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-[#FAF8F5] p-8" data-testid="forgot-password-page">
      <div className="w-full max-w-md animate-fade-in">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-[#5C5855] hover:text-[#D3735A] mb-6" data-testid="back-to-login">
          <ArrowLeft className="h-4 w-4" /> Back to login
        </Link>

        {sent ? (
          <div className="bg-white border border-[#E8E3DD] rounded-xl p-8 text-center" data-testid="reset-sent-message">
            <CheckCircle className="h-12 w-12 text-[#2C5545] mx-auto mb-4" />
            <h2 className="font-['Manrope'] text-2xl font-bold text-[#2D2B2A] mb-2">Check your email</h2>
            <p className="text-[#5C5855]">If an account exists with <strong>{email}</strong>, we've sent a password reset link.</p>
            <p className="text-sm text-[#8A847F] mt-4">The link expires in 1 hour.</p>
          </div>
        ) : (
          <>
            <h2 className="font-['Manrope'] text-3xl font-bold text-[#2D2B2A] mb-2">Forgot password?</h2>
            <p className="text-[#5C5855] mb-8">Enter your email and we'll send you a reset link.</p>

            {error && (
              <div className="mb-6 p-4 bg-[#B74134]/10 border border-[#B74134]/20 rounded-lg text-sm text-[#B74134]" data-testid="forgot-error">{error}</div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Email address</Label>
                <Input
                  data-testid="forgot-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="bg-white border-[#D1C9C0] focus:ring-2 focus:ring-[#D3735A]/50 focus:border-[#D3735A]"
                />
              </div>
              <Button data-testid="forgot-submit-btn" type="submit" disabled={submitting}
                className="w-full bg-[#D3735A] hover:bg-[#BD6047] text-white rounded-lg px-6 py-3 font-semibold h-12">
                {submitting ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 3000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#FAF8F5] p-8">
        <div className="text-center">
          <h2 className="font-['Manrope'] text-2xl font-bold text-[#2D2B2A] mb-2">Invalid Reset Link</h2>
          <p className="text-[#5C5855] mb-4">This reset link is invalid or has expired.</p>
          <Link to="/forgot-password" className="text-[#D3735A] hover:text-[#BD6047] font-semibold">Request a new one</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-[#FAF8F5] p-8" data-testid="reset-password-page">
      <div className="w-full max-w-md animate-fade-in">
        {success ? (
          <div className="bg-white border border-[#E8E3DD] rounded-xl p-8 text-center" data-testid="reset-success-message">
            <CheckCircle className="h-12 w-12 text-[#2C5545] mx-auto mb-4" />
            <h2 className="font-['Manrope'] text-2xl font-bold text-[#2D2B2A] mb-2">Password Reset!</h2>
            <p className="text-[#5C5855]">Your password has been reset. Redirecting to login...</p>
          </div>
        ) : (
          <>
            <h2 className="font-['Manrope'] text-3xl font-bold text-[#2D2B2A] mb-2">Set new password</h2>
            <p className="text-[#5C5855] mb-8">Enter your new password below.</p>

            {error && (
              <div className="mb-6 p-4 bg-[#B74134]/10 border border-[#B74134]/20 rounded-lg text-sm text-[#B74134]" data-testid="reset-error">{error}</div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label className="block text-sm font-semibold text-[#5C5855] mb-2">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A847F]" />
                  <Input data-testid="new-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min 6 characters" required minLength={6} className="pl-10 bg-white border-[#D1C9C0] focus:ring-2 focus:ring-[#D3735A]/50" />
                </div>
              </div>
              <div>
                <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A847F]" />
                  <Input data-testid="confirm-password-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repeat password" required className="pl-10 bg-white border-[#D1C9C0] focus:ring-2 focus:ring-[#D3735A]/50" />
                </div>
              </div>
              <Button data-testid="reset-submit-btn" type="submit" disabled={submitting}
                className="w-full bg-[#D3735A] hover:bg-[#BD6047] text-white rounded-lg px-6 py-3 font-semibold h-12">
                {submitting ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
